/**
 * Mesas de un día: primero las pendientes, después las impresas
 */

import type { DeskOrder } from '@/services/deskService';
import { PendingTableCard } from './PendingTableCard';
import { PrintedTableTile } from './PrintedTableTile';
import { ReprintBar } from './ReprintBar';
import { isPrinted } from './orderDisplay';

interface DayTablesProps {
  orders: DeskOrder[];
  printingId: string | null;
  reprintId: string | null;
  canManageItems: boolean;
  onPrint: (order: DeskOrder, copies: number) => void;
  onSelectReprint: (orderId: string | null) => void;
}

export function DayTables({
  orders,
  printingId,
  reprintId,
  canManageItems,
  onPrint,
  onSelectReprint,
}: DayTablesProps) {
  const pending = orders.filter((order) => !isPrinted(order));
  const printed = orders.filter(isPrinted);
  const reprintOrder = printed.find((order) => order.id === reprintId);

  return (
    <div className="grid gap-4">
      {pending.length > 0 && (
        <div className="grid gap-3">
          <SectionLabel>Por imprimir ({pending.length})</SectionLabel>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
            {pending.map((order) => (
              <PendingTableCard
                key={order.id}
                order={order}
                isPrinting={printingId === order.id}
                canManageItems={canManageItems}
                onPrint={(copies) => onPrint(order, copies)}
              />
            ))}
          </div>
        </div>
      )}

      {printed.length > 0 && (
        <div className="grid gap-3">
          <SectionLabel>Impresas ({printed.length})</SectionLabel>
          {reprintOrder && (
            <ReprintBar
              key={reprintOrder.id}
              order={reprintOrder}
              isPrinting={printingId === reprintOrder.id}
              onPrint={(copies) => onPrint(reprintOrder, copies)}
              onCancel={() => onSelectReprint(null)}
            />
          )}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-3">
            {printed.map((order) => (
              <PrintedTableTile
                key={order.id}
                order={order}
                isSelected={order.id === reprintId}
                onSelect={() => onSelectReprint(order.id === reprintId ? null : order.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-base font-medium text-gray-700">{children}</h3>
  );
}
